/**
 * Image Search Agent Parameter Schema
 * 
 * IMPORTANT: All array and boolean types must have explicit descriptions
 * to help the LLM generate correct JSON types (not stringified values).
 */

import { z } from "zod";
import { SubAgentParams } from "../../../core/types";
import { documentIdSchema } from "@carousel-office/agent-runtime/excelEditor/agents/search/image/tools/searchPresentationImages/searchPresentationImages.schema";

// ═══════════════════════════════════════════════════════════════════════════
// REQUIREMENT ITEM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Preferred logo/image shape.
 * - "square": icon-only, roughly 1:1 aspect ratio (good for grid cells, table cells)
 * - "horizontal": wide logo with text (good for banners, headers, two-column grids)
 * - "any": no preference
 */
export const ImageShapePreferenceEnum = z.enum(["square", "horizontal", "any"]);
export type ImageShapePreference = z.infer<typeof ImageShapePreferenceEnum>;

/**
 * Preferred background/theme for logo.
 * - "light_bg": logo will be placed on a light/white background → prefer dark/colored logos
 * - "dark_bg": logo will be placed on a dark background → prefer white/light logos
 * - "any": no preference
 */
export const ImageBackgroundPreferenceEnum = z.enum(["light_bg", "dark_bg", "any"]);
export type ImageBackgroundPreference = z.infer<typeof ImageBackgroundPreferenceEnum>;

/**
 * Single image requirement
 */
export const ImageRequirementItemSchema = z.object({
    id: z.string().describe("Unique identifier like 'A', 'B', 'C'"),
    requirement: z.string().describe("Natural language image description"),
    preferredShape: ImageShapePreferenceEnum.optional()
        .describe("Optional shape preference: 'square' (icon, 1:1), 'horizontal' (wide logo with text), or 'any'. Helps select the best logo variant."),
    preferredBackground: ImageBackgroundPreferenceEnum.optional()
        .describe("Optional background context: 'light_bg' (logo on white/light bg → prefer dark logos), 'dark_bg' (logo on dark bg → prefer light logos), or 'any'."),
});

export type ImageRequirementItem = z.infer<typeof ImageRequirementItemSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT ITEM SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Context item for providing relevant context to the agent
 */
export const ImageContextItemSchema = z.object({
    context: z.array(z.string()).describe("Array of short IDs (slide_*, logo_*, etc.)"),
    purpose: z.string().describe("How this context relates to the search"),
});

export type ImageContextItem = z.infer<typeof ImageContextItemSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const ImageSearchParamsSchema = z.object({
    // Base params - explicit descriptions for proper JSON generation
    requirements: z.array(ImageRequirementItemSchema)
        .min(1, "At least one requirement is required")
        .describe("Array of image requirements to fulfill. Must be a JSON array, not a string."),
    uiDescription: z.string()
        .describe("Single-line UI description for top-of-card agent status display."),
    uiDescriptions: z.array(z.string())
        .optional()
        .describe("Optional list of UI description lines for the top section of agent-as-tool cards."),
    
    context: z.array(ImageContextItemSchema)
        .optional()
        .describe("Optional array of context items with short IDs from prior searches or reference slides"),
    
    threadId: z.string()
        .optional()
        .describe("Optional thread ID for conversation continuation"),
    
    // Image Search-specific
    documentId: documentIdSchema
        .optional()
        .describe("Optional deck reference for presentation image search: numeric index (0, 1, 2), 'CURRENT', or deck ID"),
});

// Extend SubAgentParams to ensure type compatibility
// Override requirements to include image-specific fields (preferredShape, preferredBackground)
export interface ImageSearchParams extends Omit<SubAgentParams, 'requirements'> {
    /** Image requirements with optional shape/background preferences */
    requirements: ImageRequirementItem[];
    /** Optional deck reference for searching presentation images */
    documentId?: string | number;
}

